import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import QualAPI from 'utils/QualAPI';
import React from 'react';
import { BOT_USER_ID } from 'constants/UserConstants';
import { normalizeId } from '../utils/idNormalization';

// Define query keys for users
export const userKeys = {
  all: ['users'],
  lists: () => [...userKeys.all, 'list'],
  // Normalized so a string id from useParams and a number from
  // useCurrentProject hit the same cache entry instead of refetching.
  list: (projectId) => [...userKeys.lists(), normalizeId(projectId)],
  details: () => [...userKeys.all, 'detail'],
  detail: (id) => [...userKeys.details(), id],
  current: () => [...userKeys.all, 'current'],
};

/**
 * Hook to fetch the current authenticated user
 * @returns {Object} The query result containing current user data, loading state, and error
 */
export function useCurrentUser() {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: userKeys.current(),
    queryFn: () => QualAPI.fetchUser()
      .then(response => {
        // Cache the user individually so it can be retrieved with useUser
        if (response.data && response.data.id) {
          queryClient.setQueryData(
            userKeys.detail(response.data.id),
            response.data
          );
        }
        return response.data;
      }),
    staleTime: 1000 * 60 * 5, // 5 minutes - user data doesn't change often
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch collaborators/users for a project and store them individually in the cache
 * @param {string|number} projectId - The ID of the project to fetch collaborators for
 * @returns {Object} The query result containing users data, loading state, and error
 */
export function useProjectUsers(projectId) {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: userKeys.list(projectId),
    queryFn: () => QualAPI.getCollabList(projectId)
      .then(response => {
        // Store each user individually in the cache for later retrieval
        if (response.data) {
          // Cache the owner
          if (response.data.owner && response.data.owner.id) {
            queryClient.setQueryData(
              userKeys.detail(response.data.owner.id),
              response.data.owner
            );
          }
          
          // Cache each collaborator
          if (response.data.collaborators && Array.isArray(response.data.collaborators)) {
            response.data.collaborators.forEach(collaborator => {
              if (collaborator && collaborator.id) {
                queryClient.setQueryData(
                  userKeys.detail(collaborator.id),
                  collaborator
                );
              }
            });
          }
        }
        return response.data;
      }),
    enabled: !!projectId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Returns query options for fetching a user by ID
 * @param {string|number} userId - The ID of the user to get from cache
 * @param {string|number} projectId - Optional project ID to look up users from specific project cache
 * @param {Object} queryClient - The query client instance
 * @returns {Object} Query options object
 */
export function getUserQueryOptions(userId, projectId, queryClient) {
  const normalizedUserId = userId ? parseInt(userId, 10) : null;

  return {
    queryKey: userKeys.detail(normalizedUserId),
    queryFn: async () => {
      // Handle bot user
      if (userId === BOT_USER_ID) {
        return {
          id: BOT_USER_ID,
          name: 'AI Assistant',
          email: 'ai@delvetool.com'
        };
      }

      // Check current user cache
      const currentUserData = queryClient.getQueryData(userKeys.current());
      if (currentUserData && parseInt(currentUserData.id, 10) === normalizedUserId) {
        return currentUserData;
      }

      // Check specific project's user list cache
      if (projectId) {
        const projectUsersData = queryClient.getQueryData(userKeys.list(projectId));

        if (projectUsersData) {
          // Check owner
          if (projectUsersData.owner && parseInt(projectUsersData.owner.id, 10) === normalizedUserId) {
            return projectUsersData.owner;
          }

          // Check collaborators array
          if (projectUsersData.collaborators && Array.isArray(projectUsersData.collaborators)) {
            const user = projectUsersData.collaborators.find(
              collaborator => parseInt(collaborator.id, 10) === normalizedUserId
            );
            if (user) return user;
          }
        }
      }

      throw new Error(`User ${userId} not found in cache`);
    },
    enabled: !!normalizedUserId || userId === BOT_USER_ID,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: false,
    refetchOnWindowFocus: false,
  };
}

/**
 * Hook to get user data by ID from cache (populated by useCurrentUser or useProjectUsers)
 * @param {string|number} userId - The ID of the user to get from cache
 * @param {string|number} projectId - Optional project ID to look up users from specific project cache
 * @returns {Object} The query result containing user data
 */
export function useUser(userId, projectId = null) {
  const queryClient = useQueryClient();
  return useQuery(getUserQueryOptions(userId, projectId, queryClient));
}

/**
 * Hook to get multiple users by their IDs using React Query's useQueries
 * @param {Array<string|number>} userIds - Array of user IDs to fetch
 * @param {string|number} projectId - Optional project ID for cache lookup context
 * @returns {Object} Object containing userMap, isLoading, and hasErrors
 */
export function useUsers(userIds = [], projectId = null) {
  const queryClient = useQueryClient();

  // Use useQueries to fetch all users in parallel
  const userQueries = useQueries({
    queries: userIds.map(userId => getUserQueryOptions(userId, projectId, queryClient)),
  });

  // Build user map from query results
  const userMap = React.useMemo(() => {
    const users = {};

    userQueries.forEach((query, index) => {
      const userId = userIds[index];
      if (query.data) {
        // For numeric IDs, normalize them
        const key = userId === BOT_USER_ID ? BOT_USER_ID : parseInt(userId, 10);
        users[key] = query.data;
      }
    });

    return users;
  }, [userQueries, userIds]);

  const isLoading = userQueries.some(query => query.isLoading);
  const hasErrors = userQueries.some(query => query.isError);

  return {
    userMap,
    isLoading,
    hasErrors,
  };
}